import { isPresent } from "@suse-o11y/is-present";
import type { Logger } from "@suse-o11y/logger";
import { type Either, left, right } from "fp-ts/es6/Either";
import type { Type } from "io-ts";
import {
	type QueryParamConfig,
	decodeDelimitedNumericArray,
	encodeDelimitedNumericArray,
} from "serialize-query-params";

import type { NodeId } from "~/models/dto";

import type { LocationState } from "../app/state/route/RouteReducer";

export interface UnexpectedRouteError {
	_type: "UnexpectedRouteError";
	expectedRoute: string;
}

export const unexpectedRouteError = (expectedRoute: string): UnexpectedRouteError => ({
	_type: "UnexpectedRouteError",
	expectedRoute,
});

interface RouteParamValidationError {
	_type: "RouteParamValidationError";
	paramKey: string;
	paramValue: string;
	expectedType?: Type<any>;
}

const routeParamValidationError = (
	paramKey: string,
	paramValue: string,
	expectedType?: Type<any>,
): RouteParamValidationError => ({
	_type: "RouteParamValidationError",
	paramKey,
	paramValue,
	expectedType,
});

type RouteError = UnexpectedRouteError | RouteParamValidationError;

function routeErrorToString(routeError: RouteError): string {
	return `Route error: ${JSON.stringify(routeError)}`;
}

export type RouteResult<R> = Either<RouteError, R>;

export function getOptionalSearchParam<T>(
	location: LocationState,
	key: string,
	type: Type<T>,
): RouteResult<T | undefined> {
	const searchParams = new URLSearchParams(location.search);
	const searchVal = searchParams.get(key);
	if (!searchVal) {
		return right(undefined);
	}

	return type.decode(searchVal).fold(() => left(routeParamValidationError(key, searchVal, type)), right);
}

export function getRouteResultOrLog<T>(routeResult: RouteResult<T>, log: Logger): T | undefined {
	if (routeResult.isRight()) {
		return routeResult.value;
	}
	log.error(routeErrorToString(routeResult.value));
	return undefined;
}

export const encodeNodeIdArray = (nodeIds: Array<NodeId> | undefined): undefined | string =>
	encodeDelimitedNumericArray(nodeIds, ",") ?? undefined;

const decodeNodeIdArray = (input: string | undefined): undefined | Array<NodeId> => {
	const arr = decodeDelimitedNumericArray(input, ",");

	if (arr == null) {
		return undefined;
	}

	const nodeIdArr = arr.filter(isPresent).filter(isFinite);

	// decoding fails in case if the decoded array was not empty but filtered finite values are empty
	if (arr.length !== 0 && nodeIdArr.length === 0) {
		return undefined;
	}

	return nodeIdArr as Array<NodeId>;
};

export type SelectionParamConfig = QueryParamConfig<Array<NodeId> | undefined>;
export const NodeIdArrayParam: SelectionParamConfig = {
	encode: encodeNodeIdArray,
	decode: decodeNodeIdArray,
};
